import { useState } from 'react';
import { ANY_EMPLOYEE_ID, getAnyEmployee } from '../constants';
import type { SelectEmployeeListItem } from '../views/SelectEmployeeList.types';
import EmployeeInfoModal from './EmployeeInfoModal';
import SelectEmployeeCard from './SelectEmployeeCard';

export type SelectedEmployeeInfoProps = {
  heading: string;

  /** Bookable employees, already filtered + normalized. Excludes "Valfri utövare". */
  employees: SelectEmployeeListItem[];

  /** Currently selected employee id, or `ANY_EMPLOYEE_ID` for "Valfri utövare". */
  selectedEmployeeId?: number;

  /** Takes over the (i): the section opens no modal of its own and hands the id to the caller. */
  onOpenInfo?: (employeeId: number) => void;
};

/**
 * The "Vald utövare" section above the add-service list: who the services being added will
 * be booked with. Read-only — the card answers nothing, so it carries neither radio nor
 * chevron, and switching stays where the selection is owned (the calendar's side column).
 * Its only output is the (i) button, which opens the employee's presentation text.
 */
const SelectedEmployeeInfo = ({ heading, employees, selectedEmployeeId, onOpenInfo }: SelectedEmployeeInfoProps) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const selected = employees.find((employee) => employee.id === selectedEmployeeId);
  // A stale selection naming an employee who performs none of these services has no card to
  // show, and no card is better than one contradicting what the list is filtered by.
  if (!selected && selectedEmployeeId !== ANY_EMPLOYEE_ID) return null;

  const anyEmployee = getAnyEmployee();
  // "Valfri utövare" is a choice, not a person: the icon tile stands in for an avatar and
  // there is no presentation text to open.
  const card = selected
    ? { name: selected.name, jobTitle: selected.jobTitle, avatarUrl: selected.avatarUrl, rating: selected.rating }
    : { name: anyEmployee.about.name, jobTitle: anyEmployee.about.jobTitle, avatarIcon: 'users-alt' as const };

  return (
    // gap-md, not the switching panel's gap-sm: Figma node 444-48512 puts the card 16px
    // (Margin/MarginM) below the heading.
    <section aria-label={heading} className="gap-md flex flex-col">
      <h2 className="text-fg_primary text-xl font-bold">{heading}</h2>
      <SelectEmployeeCard
        {...card}
        onInfoClick={selected?.info ? () => (onOpenInfo ? onOpenInfo(selected.id) : setIsInfoOpen(true)) : undefined}
      />
      {!onOpenInfo && (
        <EmployeeInfoModal employee={isInfoOpen ? selected : undefined} onClose={() => setIsInfoOpen(false)} />
      )}
    </section>
  );
};

export default SelectedEmployeeInfo;
